/**
 * <bigens-usp-modal> (T41a): the "i" button on the softness cell of the PDP USP strip and its explainer <dialog>.
 *
 * - Opens with showModal(): the browser traps focus inside the dialog and makes the rest of the page inert.
 * - Closes on Esc (native cancel), on the close button, or on a click on the backdrop (the dialog element itself;
 *   the card inside fills the dialog's box, so clicks on the content never reach the dialog).
 * - Focus returns to whatever had it before opening (normally the "i" button); aria-expanded mirrors the state.
 * - Listeners are delegated on the host, so the markup can be re-rendered by <bigens-stage-sync> (another size,
 *   another softness value) without re-wiring. Labels and copy come from Liquid; there is no text here.
 */
class BigensUspModal extends HTMLElement {
  /** @type {Element | null} */
  #restoreTo = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('close', this.#onClose, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('close', this.#onClose, true);
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return this.querySelector('dialog');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const dialog = this.#dialog;
    if (!dialog) return;

    if (target.closest('[data-usp-open]')) {
      event.preventDefault();
      this.#open(dialog);
      return;
    }
    if (target.closest('[data-usp-close]')) {
      event.preventDefault();
      dialog.close();
      return;
    }
    if (target === dialog && dialog.open) {
      dialog.close();
    }
  };

  /** @param {HTMLDialogElement} dialog */
  #open(dialog) {
    if (dialog.open || typeof dialog.showModal !== 'function') return;
    this.#restoreTo = document.activeElement;
    dialog.showModal();
    this.querySelector('[data-usp-open]')?.setAttribute('aria-expanded', 'true');
  }

  #onClose = () => {
    const opener = this.querySelector('[data-usp-open]');
    opener?.setAttribute('aria-expanded', 'false');
    const previous = this.#restoreTo;
    const restore =
      previous instanceof HTMLElement && previous.isConnected && previous !== document.body ? previous : opener;
    this.#restoreTo = null;
    if (restore instanceof HTMLElement) restore.focus({ preventScroll: true });
  };
}

if (!customElements.get('bigens-usp-modal')) {
  customElements.define('bigens-usp-modal', BigensUspModal);
}
